"use client";

import { OptimizedImage } from "@/components/ui/optimized-image";
// Locale-aware Link keeps /partner/:slug under the current locale prefix.
import { Link } from "@/i18n/navigation";
import type { Partner } from "@/types";
import { useTranslations } from "next-intl";

interface PartnerInfoDetailProps {
  partner?: Partner;
  primaryCity?: string;
}

export default function PartnerInfoDetail({
  partner,
  primaryCity,
}: PartnerInfoDetailProps) {
  const t = useTranslations("product.partner");

  if (!partner) {
    return null;
  }

  return (
    <div className="py-6 border-y">
      {/* Partner Info Row */}
      <div className="flex items-center gap-3">
        {/* Logo */}
        <div className="shrink-0">
          {partner.logo_url ? (
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-gray-200">
              <OptimizedImage
                src={partner.logo_url}
                alt={partner.name}
                width={56}
                height={56}
                className="object-cover w-full h-full"
              />
            </div>
          ) : (
            <div className="w-14 h-14 bg-linear-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold text-xl border border-gray-200">
              {partner.name?.[0]?.toUpperCase() || "P"}
            </div>
          )}
        </div>

        {/* Name and Description */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900">{partner.name}</h3>
          {partner.description && (
            <p className="text-xs text-gray-500 line-clamp-2">
              {partner.description}
            </p>
          )}
        </div>

        {/* Host Introduction Button */}
        <Link
          href={`/partner/${partner.slug}`}
          className="shrink-0 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        >
          {t("intro")}
        </Link>
      </div>
    </div>
  );
}
